import { ClientBase } from "./base.js";
import { RepairClient } from "./repair.js";
/** Staff tasks (/tasks) and the staff announcement board (/announcements). */
export class TasksClient extends RepairClient {
    taskBase(slug, id) {
        const base = `${this.siteBase(slug)}/tasks`;
        return id === undefined ? base : `${base}/${encodeURIComponent(id)}`;
    }
    async listStaffTasks(slug, params = {}) {
        const res = await this.request("GET", `${this.taskBase(slug)}${ClientBase.query({ board: params.board, updatedSince: params.updatedSince })}`, undefined, true);
        return res.tasks;
    }
    /** Supervisors (employees:manage) only. */
    async createStaffTask(slug, input) {
        return this.request("POST", this.taskBase(slug), input, true);
    }
    async getStaffTask(slug, id) {
        return this.request("GET", this.taskBase(slug, id), undefined, true);
    }
    /** `updatedAt` required; 409 `stale_update` when changed. */
    async patchStaffTask(slug, id, input) {
        return this.request("PATCH", this.taskBase(slug, id), input, true);
    }
    async deleteStaffTask(slug, id) {
        await this.request("DELETE", this.taskBase(slug, id), undefined, true);
    }
    async joinTaskAsMe(slug, id) {
        return this.request("POST", `${this.taskBase(slug, id)}/assignees/me`, undefined, true);
    }
    async leaveTaskAsMe(slug, id) {
        return this.request("DELETE", `${this.taskBase(slug, id)}/assignees/me`, undefined, true);
    }
    /** Supervisors (employees:manage): assigns an active staff member. 404 when the user is not active staff. */
    async assignTaskUser(slug, id, userId) {
        return this.request("POST", `${this.taskBase(slug, id)}/assignees`, { userId }, true);
    }
    /** Supervisors (employees:manage). Idempotent, 204. */
    async unassignTaskUser(slug, id, userId) {
        await this.request("DELETE", `${this.taskBase(slug, id)}/assignees/${encodeURIComponent(userId)}`, undefined, true);
    }
    /** Stamps the caller's `seenAt` (idempotent; does not bump the task `updatedAt`). 404 when the caller is not on the task. */
    async markTaskSeen(slug, id) {
        return this.request("POST", `${this.taskBase(slug, id)}/assignees/me/seen`, undefined, true);
    }
    async addTaskNote(slug, id, body) {
        return this.request("POST", `${this.taskBase(slug, id)}/notes`, { body }, true);
    }
    /** Only the note's author may edit. */
    async patchTaskNote(slug, id, noteId, body) {
        return this.request("PATCH", `${this.taskBase(slug, id)}/notes/${encodeURIComponent(noteId)}`, { body }, true);
    }
    async deleteTaskNote(slug, id, noteId) {
        return this.request("DELETE", `${this.taskBase(slug, id)}/notes/${encodeURIComponent(noteId)}`, undefined, true);
    }
    // Announcements (staff audience only)
    /** `includeExpired` is honoured only for supervisors. */
    async listAnnouncements(slug, params = {}) {
        const res = await this.request("GET", `${this.siteBase(slug)}/announcements${ClientBase.query({ includeExpired: params.includeExpired ? "true" : undefined })}`, undefined, true);
        return res.announcements;
    }
    /** employees:manage. */
    async createAnnouncement(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/announcements`, input, true);
    }
    /** employees:manage. Pin or unpin; a fourth pinned announcement is 409. */
    async setAnnouncementPinned(slug, id, pinned) {
        return this.request("PATCH", `${this.siteBase(slug)}/announcements/${encodeURIComponent(id)}`, { pinned }, true);
    }
    /** employees:manage. 204. */
    async deleteAnnouncement(slug, id) {
        await this.request("DELETE", `${this.siteBase(slug)}/announcements/${encodeURIComponent(id)}`, undefined, true);
    }
    // Web push (staff). 404 on public-key/subscriptions when the deployment has no VAPID keys.
    async getPushPublicKey(slug) {
        const res = await this.request("GET", `${this.siteBase(slug)}/push/public-key`, undefined, true);
        return res.publicKey;
    }
    /** Saves the browser subscription; the same endpoint replaces the earlier row. */
    async subscribePush(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/push/subscriptions`, input, true);
    }
    /** 204, also when nothing matched. */
    async unsubscribePush(slug, endpoint) {
        await this.request("DELETE", `${this.siteBase(slug)}/push/subscriptions${ClientBase.query({ endpoint })}`, undefined, true);
    }
    /** Role routing per notification type (stored overrides over the built-in defaults). */
    async getPushRules(slug) {
        const res = await this.request("GET", `${this.siteBase(slug)}/push/rules`, undefined, true);
        return res.rules;
    }
    /** Tenant owner only. Changes only the named types; resolves with the full rules. */
    async setPushRules(slug, rules) {
        const res = await this.request("PUT", `${this.siteBase(slug)}/push/rules`, { rules }, true);
        return res.rules;
    }
}
//# sourceMappingURL=tasks.js.map